import { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ClockIcon,
  ArrowsRightIcon,
  MessageCircle01Icon,
  Users01Icon,
  FaceHappyIcon,
  CalendarIcon,
  TrendUp01Icon,
  XCircleIcon,
  Edit01Icon,
  CurrencyEuroIcon,
} from '@untitledui-icons/react/line'

import { useIdentity } from '@/hooks/api'
import { useDashboardStats } from '@/hooks/api/useDashboardStats'
import { PageHeader } from '@/components/layout'
import { cn } from '@/lib/utils'
import type { StatsFilters } from '@/types/dashboard'

import { ConversationStatsChart, DashboardFilters } from './components'

// Parse filters from URL search params
function parseFiltersFromUrl(searchParams: URLSearchParams): StatsFilters {
  const assistantIds = searchParams.get('assistant_ids')
  const customerIds = searchParams.get('customer_ids')
  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')
  const shortcutLabel = searchParams.get('shortcut')

  return {
    assistant_ids: assistantIds
      ? assistantIds.split(',').map(Number).filter(Boolean)
      : undefined,
    customer_ids: customerIds
      ? customerIds.split(',').map(Number).filter(Boolean)
      : undefined,
    start_date: startDate ? new Date(startDate) : undefined,
    end_date: endDate ? new Date(endDate) : undefined,
    shortcutLabel: shortcutLabel || undefined,
  }
}

// Serialize filters to URL search params
function serializeFiltersToUrl(filters: StatsFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.assistant_ids?.length) {
    params.set('assistant_ids', filters.assistant_ids.join(','))
  }
  if (filters.customer_ids?.length) {
    params.set('customer_ids', filters.customer_ids.join(','))
  }
  if (filters.start_date) {
    params.set('start_date', filters.start_date.toISOString())
  }
  if (filters.end_date) {
    params.set('end_date', filters.end_date.toISOString())
  }
  if (filters.shortcutLabel) {
    params.set('shortcut', filters.shortcutLabel)
  }
  return params
}

// Format duration in seconds to human-readable string
function formatDuration(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const pad = (num: number) => num.toString().padStart(2, '0')

  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${pad(minutes)}m`
  if (minutes > 0) return `${minutes}m`
  return `${Math.floor(totalSeconds)}s`
}

// Format number with locale-aware formatting
// Map i18n language codes to full locale codes for number formatting
const LOCALE_MAP: Record<string, string> = {
  nl: 'nl-NL',
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
}

// Create formatters for a specific locale
function createFormatters(locale: string) {
  const fullLocale = LOCALE_MAP[locale] || locale || 'nl-NL'
  
  return {
    formatNumber: (value: number): string => {
      return new Intl.NumberFormat(fullLocale, { maximumFractionDigits: 0 }).format(value)
    },
    formatCurrency: (value: number): string => {
      return new Intl.NumberFormat(fullLocale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(value)
    },
  }
}

const PRICE_PER_GUEST_KEY = 'dashboard_price_per_guest'
const DEFAULT_PRICE_PER_GUEST = 50

export function DashboardPage() {
  const { t, i18n } = useTranslation()
  const { user } = useIdentity()
  const [searchParams, setSearchParams] = useSearchParams()

  // Create locale-aware formatters based on current language
  const { formatNumber, formatCurrency } = useMemo(
    () => createFormatters(i18n.language),
    [i18n.language]
  )

  // Price per guest state with localStorage persistence
  const [pricePerGuest, setPricePerGuest] = useState<number>(() => {
    if (typeof window === 'undefined') return DEFAULT_PRICE_PER_GUEST
    const stored = localStorage.getItem(PRICE_PER_GUEST_KEY)
    return stored ? parseFloat(stored) : DEFAULT_PRICE_PER_GUEST
  })
  const [isEditingPrice, setIsEditingPrice] = useState(false)
  const [tempPrice, setTempPrice] = useState(pricePerGuest.toString())

  // Persist price to localStorage
  useEffect(() => {
    localStorage.setItem(PRICE_PER_GUEST_KEY, pricePerGuest.toString())
  }, [pricePerGuest])

  const handlePriceSubmit = () => {
    const newPrice = parseFloat(tempPrice)
    if (!isNaN(newPrice) && newPrice >= 0) {
      setPricePerGuest(newPrice)
    } else {
      setTempPrice(pricePerGuest.toString())
    }
    setIsEditingPrice(false)
  }

  const filters = useMemo(() => parseFiltersFromUrl(searchParams), [searchParams])
  const { data: statsData, isLoading: statsLoading } = useDashboardStats({ filters })

  const handleFilterChange = useCallback(
    (newFilters: StatsFilters) => setSearchParams(serializeFiltersToUrl(newFilters)),
    [setSearchParams]
  )

  const totalStats = statsData?.total_stats
  const dailyStats = statsData?.daily_stats

  const transferPercentage =
    totalStats?.call_count && totalStats?.transferred_count
      ? Math.round((totalStats.transferred_count / totalStats.call_count) * 100)
      : 0

  const transferTimePercentage =
    totalStats?.call_duration && totalStats?.transfer_duration
      ? Math.round((totalStats.transfer_duration / totalStats.call_duration) * 100)
      : 0

  const firstName = user?.first_name || t('resources.dashboard.user_fallback')

  // Check if reservation data exists
  const hasReservationData =
    (totalStats?.reservation_count ?? 0) > 0 ||
    (totalStats?.cancellation_count ?? 0) > 0 ||
    (totalStats?.total_updates ?? 0) > 0

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${t('resources.dashboard.welcome_back')}, ${firstName}! 👋`}
        subtitle={t('resources.dashboard.whats_happening')}
        variant="simple"
      />

      {/* Filters */}
      <DashboardFilters filters={filters} onFilterChange={handleFilterChange} />

      {statsLoading ? (
        <DashboardSkeleton />
      ) : (
        <div className="space-y-6">
          {/* Hero Stats Row - Large prominent cards */}
          <div className="grid gap-4 md:grid-cols-3">
            {/* Call Time - Hero Card */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 p-6 text-white shadow-lg transition-all hover:shadow-xl hover:shadow-brand-500/25">
              <div className="absolute -right-8 -top-8 size-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-4 -right-4 size-24 rounded-full bg-white/5" />
              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur-sm">
                    <ClockIcon className="size-6" />
                  </div>
                  <span className="text-sm font-medium text-white/80">
                    {t('resources.conversations.stats.call_time')}
                  </span>
                </div>
                <p className="text-4xl font-bold tracking-tight">
                  {formatDuration(totalStats?.call_duration || 0)}
                </p>
                <p className="mt-2 text-sm text-white/70">
                  {formatNumber(totalStats?.call_count || 0)} {t('resources.conversations.stats.calls_handled')}
                </p>
              </div>
            </div>

            {/* Messages - Hero Card */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-500 via-violet-600 to-purple-700 p-6 text-white shadow-lg transition-all hover:shadow-xl hover:shadow-violet-500/25">
              <div className="absolute -right-8 -top-8 size-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-4 -right-4 size-24 rounded-full bg-white/5" />
              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur-sm">
                    <MessageCircle01Icon className="size-6" />
                  </div>
                  <span className="text-sm font-medium text-white/80">
                    {t('resources.conversations.stats.total_messages')}
                  </span>
                </div>
                <p className="text-4xl font-bold tracking-tight">
                  {formatNumber(totalStats?.message_count || 0)}
                </p>
                <p className="mt-2 text-sm text-white/70">
                  {t('resources.dashboard.whatsapp_sms')}
                </p>
              </div>
            </div>

            {/* Customer Satisfaction - Hero Card */}
            <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-6 text-white shadow-lg transition-all hover:shadow-xl hover:shadow-emerald-500/25">
              <div className="absolute -right-8 -top-8 size-32 rounded-full bg-white/10" />
              <div className="absolute -bottom-4 -right-4 size-24 rounded-full bg-white/5" />
              <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-white/20 p-2.5 backdrop-blur-sm">
                    <FaceHappyIcon className="size-6" />
                  </div>
                  <span className="text-sm font-medium text-white/80">
                    {t('resources.conversations.stats.csat_score')}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold tracking-tight">
                    {Math.round(totalStats?.csat_score || 0)}%
                  </p>
                  <TrendUp01Icon className="size-5 text-white/80" />
                </div>
                <p className="mt-2 text-sm text-white/70">
                  {t('resources.dashboard.customer_satisfaction')}
                </p>
              </div>
            </div>
          </div>

          {/* Transfer Stats Row */}
          <div className="rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100 p-6 dark:from-slate-800/50 dark:to-slate-900/50">
            <div className="mb-4 flex items-center gap-2">
              <ArrowsRightIcon className="size-5 text-slate-600 dark:text-slate-400" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('resources.conversations.stats.transfer_metrics')}
              </h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Transfer Rate (by count) */}
              <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('resources.conversations.stats.transfer_count_percentage')}
                  </span>
                  <Users01Icon className="size-4 text-slate-400" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {transferPercentage}%
                </p>
                {/* Progress bar */}
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      transferPercentage <= 50
                        ? 'bg-success-500'
                        : 'bg-warning-500'
                    )}
                    style={{ width: `${Math.min(transferPercentage, 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {formatNumber(totalStats?.transferred_count || 0)} {t('resources.conversations.stats.total_transfers').toLowerCase()}
                </p>
              </div>

              {/* Transfer Rate (by time) */}
              <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-slate-800">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {t('resources.conversations.stats.transfer_duration_percentage')}
                  </span>
                  <ClockIcon className="size-4 text-slate-400" />
                </div>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {transferTimePercentage}%
                </p>
                {/* Progress bar */}
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      transferTimePercentage <= 60
                        ? 'bg-success-500'
                        : 'bg-warning-500'
                    )}
                    style={{ width: `${Math.min(transferTimePercentage, 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {formatDuration(totalStats?.transfer_duration || 0)} {t('resources.conversations.stats.total_transfer_time').toLowerCase()}
                </p>
              </div>
            </div>
          </div>

          {/* Chart and Reservations Row */}
          <div className="grid gap-6 lg:grid-cols-5">
            {/* Chart - Takes 3 columns (or 5 if no reservation data) */}
            <div className={hasReservationData ? 'lg:col-span-3' : 'lg:col-span-5'}>
              <ConversationStatsChart data={dailyStats} />
            </div>

            {/* Reservations - Takes 2 columns */}
            {hasReservationData && (
              <div className="lg:col-span-2">
                <div className="h-full rounded-2xl bg-gradient-to-br from-sky-50 via-white to-indigo-50 p-6 shadow-sm ring-1 ring-sky-100 dark:from-slate-800 dark:via-slate-800/50 dark:to-slate-800 dark:ring-slate-700">
                  <div className="mb-5 flex items-center gap-2">
                    <CalendarIcon className="size-5 text-sky-600 dark:text-sky-400" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {t('resources.conversations.stats.reservation_stats')}
                    </h3>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Reservations */}
                    <div className="rounded-xl bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:bg-slate-700/50">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="size-2 rounded-full bg-emerald-500" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('resources.dashboard.reservation_stats.reservations')}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                        {formatNumber(totalStats?.reservation_count || 0)}
                      </p>
                    </div>

                    {/* Guests */}
                    <div className="rounded-xl bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:bg-slate-700/50">
                      <div className="mb-2 flex items-center gap-2">
                        <div className="size-2 rounded-full bg-blue-500" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('resources.dashboard.reservation_stats.guests')}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                        {formatNumber(totalStats?.reservation_people_count || 0)}
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {t('resources.dashboard.reservation_stats.avg_per_table', {
                          value: ((totalStats?.reservation_people_count || 0) / (totalStats?.reservation_count || 1)).toFixed(1),
                        })}
                      </p>
                    </div>

                    {/* Cancellations */}
                    <div className="rounded-xl bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:bg-slate-700/50">
                      <div className="mb-2 flex items-center gap-2">
                        <XCircleIcon className="size-4 text-red-500" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('resources.dashboard.reservation_stats.cancellations')}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                        {formatNumber(totalStats?.cancellation_count || 0)}
                      </p>
                    </div>

                    {/* Updates */}
                    <div className="rounded-xl bg-white/80 p-4 shadow-sm backdrop-blur-sm dark:bg-slate-700/50">
                      <div className="mb-2 flex items-center gap-2">
                        <Edit01Icon className="size-4 text-amber-500" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {t('resources.dashboard.reservation_stats.updates')}
                        </span>
                      </div>
                      <p className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                        {formatNumber(totalStats?.total_updates || 0)}
                      </p>
                    </div>

                    {/* Reservation Value */}
                    <div className="col-span-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white shadow-sm">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CurrencyEuroIcon className="size-4" />
                          <span className="text-sm font-medium text-white/90">
                            {t('resources.dashboard.reservation_stats.estimated_value')}
                          </span>
                        </div>
                        {isEditingPrice ? (
                          <div className="flex h-9 w-[200px] items-center justify-end gap-2 rounded-lg bg-white/20 px-3 text-sm font-medium backdrop-blur-sm">
                            <span className="text-white/70">{t('resources.dashboard.reservation_stats.avg_couvert')}:</span>
                            <span className="font-semibold">€</span>
                            <input
                              type="number"
                              value={tempPrice}
                              onChange={(e) => setTempPrice(e.target.value)}
                              onBlur={handlePriceSubmit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handlePriceSubmit()
                                if (e.key === 'Escape') {
                                  setTempPrice(pricePerGuest.toString())
                                  setIsEditingPrice(false)
                                }
                              }}
                              autoFocus
                              className="h-6 w-14 rounded bg-white/20 px-2 text-sm font-semibold text-white placeholder-white/50 outline-none focus:ring-1 focus:ring-white/50"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setTempPrice(pricePerGuest.toString())
                              setIsEditingPrice(true)
                            }}
                            className="group flex h-9 w-[200px] items-center justify-end gap-2 rounded-lg bg-white/20 px-3 text-sm font-medium backdrop-blur-sm transition-all hover:bg-white/30"
                            title={t('resources.dashboard.reservation_stats.click_to_edit_couvert')}
                          >
                            <span className="text-white/70">{t('resources.dashboard.reservation_stats.avg_couvert')}:</span>
                            <span className="font-semibold">{formatCurrency(pricePerGuest)}</span>
                            <Edit01Icon className="size-3.5 text-white/60 transition-colors group-hover:text-white" />
                          </button>
                        )}
                      </div>
                      <p className="text-3xl font-bold">
                        {formatCurrency(
                          (totalStats?.reservation_people_count || 0) * pricePerGuest
                        )}
                      </p>
                      <p className="mt-1 text-xs text-white/70">
                        {formatNumber(totalStats?.reservation_people_count || 0)} {t('resources.dashboard.reservation_stats.guests').toLowerCase()} × {formatCurrency(pricePerGuest)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Hero cards skeleton */}
      <div className="grid gap-4 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="h-36 animate-pulse rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800"
          />
        ))}
      </div>
      {/* Transfer stats skeleton */}
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      {/* Chart skeleton */}
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="h-[360px] animate-pulse rounded-2xl bg-slate-100 lg:col-span-3 dark:bg-slate-800" />
        <div className="h-[360px] animate-pulse rounded-2xl bg-slate-100 lg:col-span-2 dark:bg-slate-800" />
      </div>
    </div>
  )
}

export default DashboardPage
